/**
 * Shared types for the Tokenomics RTS simulation.
 *
 * The sim is a deterministic, fixed-tick lockstep engine: given the same
 * GameConfig (seed + players) and the same ordered command stream, every
 * peer computes the identical game. The browser runs it for solo skirmish;
 * the Cloudflare Durable Object sequences commands for multiplayer.
 */

// ---------------------------------------------------------------------------
// Identifiers & basics
// ---------------------------------------------------------------------------

export type EntityId = number;
export type PlayerIdx = number; // index into GameState.players; -1 = neutral

export interface Vec2 {
  x: number;
  y: number;
}

export const TICKS_PER_SECOND = 8;

// ---------------------------------------------------------------------------
// Static content keys
// ---------------------------------------------------------------------------

export type FactionId = 'hyperscale' | 'serverless' | 'openweights';

/** FinOps Framework maturity model: Crawl -> Walk -> Run */
export type Phase = 0 | 1 | 2;
export const PHASE_NAMES = ['Crawl', 'Walk', 'Run'] as const;

export type BuildingType =
  | 'hq' // Cloud Console — command center, drop-off, trains engineers
  | 'substation' // adds power capacity; pure infrastructure overhead
  | 'gpuCluster' // generates token capacity, heavy $ burn + power draw
  | 'inferenceEndpoint' // trains inference/service agents
  | 'dataWarehouse' // data drop-off + adds data storage cap
  | 'finopsCenter' // FinOps Command Center — unlocks techs, anomaly auto-remediation
  | 'cacheLayer' // prompt/context cache — aura reduces nearby agents' token burn
  | 'firewall' // defensive tower
  | 'trainingCluster' // trains premium units, runs fine-tuning jobs
  | 'syntheticData' // converts tokens into data (synthetic datasets)
  | 'adversarialLab' // OFFENSE: launches cyber ops (prompt injection, DDoS, data poisoning)
  | 'socCenter'; // DEFENSE: Security Operations Center — mitigates cyber ops, hosts security research

export type UnitType =
  | 'engineer' // builds, harvests data nodes
  | 'inferenceAgent' // serves market segments for revenue
  | 'scoutCrawler' // fast, cheap vision
  | 'redTeamAgent' // combat unit (adversarial testing gone feral)
  | 'guardrailSentinel' // defensive combat unit
  | 'loadTester' // siege: outranges towers, buildings only, helpless vs units
  | 'frontierAgent' // Run-phase heavy: expensive, huge token burn, strong
  | 'burstWorker' // SERVERLESS ONLY: 2x serve rate, expires after 90s
  | 'reservedInstance' // HYPERSCALE ONLY: tanky defender, near-zero burn (prepaid)
  | 'distilledAgent' // OPENWEIGHTS ONLY: cheap agent, half inputs, 75% revenue
  | 'superCluster' // HYPERSCALE ONLY (Run): giant tanky siege-grade hammer
  | 'spotInstance' // SERVERLESS ONLY (Run): cheap fast glass-cannon drone, expires
  | 'forkNode'; // OPENWEIGHTS ONLY (Run): dirt-cheap spammable combat swarm

export type TechId =
  | 'taggingPolicy' // Inform: +visibility, +revenue efficiency
  | 'focusAdoption' // FOCUS spec: see rivals' burn/revenue in HUD
  | 'promptCaching' // -token cost per serve
  | 'modelRouting' // route to smaller models: -token cost, -train cost
  | 'batchInference' // +serve throughput, slight latency (serve tick slower but bigger)
  | 'commitmentDiscount1' // committed spend: -building burn %, upfront $ lump
  | 'commitmentDiscount2'
  | 'anomalyDetection' // FinOps center auto-clears anomalies fast
  | 'autoscaling' // idle units burn less
  | 'unitEconomics' // +revenue per serve (price/perf clarity)
  | 'inputSanitization' // DEFENSE: fewer of your agents fall to prompt injection
  | 'rateLimiting' // DEFENSE: WAF/rate limiting — shorter DDoS outages
  | 'dataProvenance' // DEFENSE: signed data lineage — resists data poisoning
  | 'speculativeDecoding' // RUN capstone: big token-cost cut per serve
  | 'testTimeCompute'; // RUN capstone: +damage to all your combat units

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export type EntityKind = 'unit' | 'building' | 'dataNode' | 'market';

export interface EntityBase {
  id: EntityId;
  kind: EntityKind;
  owner: PlayerIdx; // -1 for dataNode/market (neutral)
  pos: Vec2; // tile coords; units may sit at fractional positions
  hp: number;
  maxHp: number;
}

export type UnitOrderKind =
  | 'idle'
  | 'move'
  | 'gather'
  | 'returnData'
  | 'serve'
  | 'attack'
  | 'build'
  | 'guard' // follow a friendly unit/building and engage nearby threats
  | 'patrol'; // march between two points, engaging threats encountered

export interface UnitOrder {
  kind: UnitOrderKind;
  target?: Vec2;
  targetId?: EntityId;
  /** patrol endpoints and current leg direction (kind === 'patrol') */
  patrolA?: Vec2;
  patrolB?: Vec2;
  patrolToB?: boolean;
}

/** Combat stance: aggressive = engage & chase; defend = engage but leash
 *  back to an anchor; passive = never auto-engage. */
export type Stance = 'aggressive' | 'defend' | 'passive';

export interface Unit extends EntityBase {
  kind: 'unit';
  type: UnitType;
  order: UnitOrder;
  /** default 'aggressive' (classic chase behavior) */
  stance?: Stance;
  /** leash point for 'defend' stance */
  anchor?: Vec2;
  /** current path (list of waypoints), consumed head-first */
  path: Vec2[];
  attackCooldown: number; // ticks until next attack allowed
  carryingData: number;
  /** ticks remaining of "runaway loop" anomaly; 0 = healthy */
  anomalyTicks: number;
  /** true while gathered at a market and actively serving */
  serving: boolean;
  /** model generation this serve-agent was trained at (Economy 3.0: the
   * market reprices it as the global frontier moves; undefined = gen 0) */
  gen?: number;
  /** stranded by a market migration: will auto-redeploy to the nearest
   * DISCOVERED market with free demand. Cleared by any manual order. */
  redeploy?: boolean;
  /** ephemeral units (Burst Worker) despawn at this tick */
  expiresTick?: number;
}

export interface Building extends EntityBase {
  kind: 'building';
  type: BuildingType;
  /** remaining construction ticks; 0 = operational */
  buildTicks: number;
  /** training queue of unit types */
  queue: UnitType[];
  /** ticks remaining on current training job */
  trainTicks: number;
  rallyPoint?: Vec2;
  /** ticks remaining knocked offline by a DDoS cyber attack; 0 = up. While
   * >0 the building produces nothing and supplies/draws no power. */
  disabledTicks?: number;
}

export interface DataNode extends EntityBase {
  kind: 'dataNode';
  remaining: number;
}

/** Market quality tier: premium segments pay a multiplier, but only to
 *  agents whose model generation is within reach of the global frontier —
 *  everyone else serves at bulk pricing (relegation, not lockout). */
export type MarketTier = 'bulk' | 'premium';

/** A market segment: a demand pool that serving agents monetize. */
export interface Market extends EntityBase {
  kind: 'market';
  /** max simultaneous serve slots across all players */
  demand: number;
  /** quality tier; undefined = bulk (see MarketTier) */
  tier?: MarketTier;
  /** sim-internal: tick at which this market next relocates */
  nextMoveTick?: number;
}

export type Entity = Unit | Building | DataNode | Market;

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

/** How hard a bot plays. Purely behavioral — no resource cheats:
 *  easy thinks slowly and under-expands, hard thinks fast and plays sharp. */
export type BotDifficulty = 'easy' | 'normal' | 'hard';

export interface PlayerConfig {
  name: string;
  faction: FactionId;
  isBot: boolean;
  /** bots only; defaults to 'normal' */
  difficulty?: BotDifficulty;
}

/** Serving doctrine: a permanent, mutually exclusive strategic choice.
 *  'rag' serves consume much more data but earn +25%; 'finetune' serves
 *  sip data and unlock fine-tuning jobs at the Training Cluster. */
export type Doctrine = 'rag' | 'finetune';

/** Cyber-operations state, per player. Cooldowns are the attacker's; a fresh
 *  offensive ability is gated until its cooldown reaches 0. `poisonedTicks`
 *  is the victim side — a data-poisoning revenue penalty counting down. */
export interface CyberState {
  /** ticks until Prompt Injection is available again */
  injectCd: number;
  /** ticks until DDoS is available again */
  ddosCd: number;
  /** ticks until Data Poisoning is available again */
  poisonCd: number;
  /** ticks of active data-poisoning revenue penalty on THIS player */
  poisonedTicks: number;
}

/** Cumulative per-player match statistics, updated inside applyTick (so they
 *  are deterministic and lockstep-safe). Feeds the end-game summary screen;
 *  everything is revealed to all players once the match is over. */
export interface PlayerStats {
  /** units that finished training (starting units don't count) */
  unitsTrained: number;
  /** enemy units destroyed by this player's units/towers */
  unitsKilled: number;
  /** own units destroyed by enemies (TTL expiry doesn't count) */
  unitsLost: number;
  /** buildings whose construction completed (the starting HQ doesn't count) */
  buildingsBuilt: number;
  /** enemy buildings destroyed by this player */
  buildingsDestroyed: number;
  /** own buildings destroyed by enemies (recycling doesn't count) */
  buildingsLost: number;
  /** gross data brought home by engineers + produced by synthetic plants
   *  (counted before the storage-cap clamp) */
  dataHarvested: number;
  /** gross tokens generated by GPU clusters (before the pool-cap clamp) */
  tokensGenerated: number;
  /** total gross serving revenue earned */
  revenueEarned: number;
  /** completed serves (inferences) across all markets — revenue is
   *  serves × dynamic price, so this is its own signal */
  inferencesServed: number;
  /** cyber-ops (prompt injection / DDoS / data poison) launched */
  cyberOpsLaunched: number;
}

/** One data series per player, sampled every `intervalSec` of sim time for
 *  the end-game graphs. Parallel arrays, indexed together with
 *  `Timeline.ticks`. */
export interface TimelineSeries {
  /** cash on hand, rounded to whole $ */
  budget: number[];
  /** smoothed revenue $/s (same EWMA the HUD shows), 0.1 precision */
  revenue: number[];
  /** unit count */
  army: number[];
  /** cumulative unitsKilled */
  kills: number[];
  /** owned building count (including under construction) */
  buildings: number[];
  /** cumulative data harvested, rounded */
  data: number[];
  /** cumulative inferences served */
  serves: number[];
}

/** A crucial match moment, marked on the end-game graphs. */
export type TimelineEventKind = 'phase' | 'doctrine' | 'model' | 'eliminated';

export interface TimelineEvent {
  tick: number;
  player: PlayerIdx;
  kind: TimelineEventKind;
  /** short marker label, e.g. "Walk", "RAG", "Gen 2" */
  label: string;
}

/** Post-game graph telemetry. ~180 samples for a max-length (15 min) game. */
export interface Timeline {
  intervalSec: number;
  ticks: number[];
  players: TimelineSeries[];
  /** phase advances, doctrine picks, model ships, eliminations — in tick order */
  events: TimelineEvent[];
}

export interface PlayerState {
  name: string;
  faction: FactionId;
  isBot: boolean;
  /** bots only; defaults to 'normal' */
  difficulty?: BotDifficulty;
  alive: boolean;
  /** cash runway, in $ */
  budget: number;
  /** stored tokens available for agents to spend */
  tokens: number;
  /** max token pool (from GPU clusters) */
  tokenCap: number;
  data: number;
  /** data storage cap (base + warehouses); gathered data beyond this is lost */
  dataCap: number;
  /** total power supplied by HQ + substations (operational only) */
  powerCap: number;
  /** total power drawn by operational buildings; draw > cap = brownout */
  powerDraw: number;
  /** chosen serving doctrine; null until the player commits */
  doctrine: Doctrine | null;
  /** cyber-ops cooldowns + active data-poison penalty */
  cyber: CyberState;
  /** completed fine-tuning jobs (each is a permanent revenue bonus).
   *  RESET when the player ships a new model generation — fine-tunes don't
   *  transfer to a new base model. RAG is unaffected. */
  fineTuneJobs: number;
  /** model generation this player's NEW serve-agents are stamped with;
   *  advanced by frontier training runs at the Training Cluster */
  modelGen: number;
  /** ticks of "launch buzz" revenue premium remaining (earned by pushing
   *  the global frontier first, not by fast-following) */
  buzzTicks: number;
  phase: Phase;
  techs: TechId[];
  /** ticks the player has been at/below $0 (bankruptcy countdown) */
  insolvencyTicks: number;
  // Rolling per-second telemetry for HUD / unit economics
  revenuePerSec: number;
  burnPerSec: number;
  tokensPerSec: number; // net token flow
  /** cumulative match statistics for the end-game summary */
  stats: PlayerStats;
}

// ---------------------------------------------------------------------------
// Map
// ---------------------------------------------------------------------------

export type Terrain = 0 | 1; // 0 = ground, 1 = blocked (rock/ridge)

export interface GameMap {
  width: number;
  height: number;
  /** row-major terrain grid */
  terrain: Terrain[];
}

// ---------------------------------------------------------------------------
// Commands (player -> sim). These are the ONLY way state changes externally.
// ---------------------------------------------------------------------------

export type Command =
  | { type: 'move'; unitIds: EntityId[]; x: number; y: number }
  | { type: 'stop'; unitIds: EntityId[] }
  | { type: 'gather'; unitIds: EntityId[]; nodeId: EntityId }
  | { type: 'serve'; unitIds: EntityId[]; marketId: EntityId }
  | { type: 'attack'; unitIds: EntityId[]; targetId: EntityId }
  | { type: 'build'; unitId: EntityId; building: BuildingType; x: number; y: number }
  /** Send engineers to resume construction of (or repair) an existing building */
  | { type: 'repair'; unitIds: EntityId[]; buildingId: EntityId }
  /** Demolish an own building for a partial refund (never the Cloud Console) */
  | { type: 'recycle'; buildingId: EntityId }
  | { type: 'train'; buildingId: EntityId; unit: UnitType }
  | { type: 'cancelTrain'; buildingId: EntityId }
  | { type: 'research'; tech: TechId }
  /** Permanent, one-time serving doctrine commitment */
  | { type: 'chooseDoctrine'; doctrine: Doctrine }
  /** Start a fine-tuning job at an operational Training Cluster (finetune doctrine only) */
  | { type: 'fineTune'; buildingId: EntityId }
  /** Start a frontier training run at an operational Training Cluster:
   * advances the player's model generation (and possibly the global frontier) */
  | { type: 'trainingRun'; buildingId: EntityId }
  | { type: 'advancePhase' }
  | { type: 'fixAnomaly'; unitId: EntityId }
  | { type: 'setStance'; unitIds: EntityId[]; stance: Stance }
  /** Cyber offense: launched from an operational Adversarial Lab at an enemy
   * building. Each has a resource cost and a cooldown (see CyberState). */
  | { type: 'promptInjection'; targetId: EntityId }
  | { type: 'ddos'; targetId: EntityId }
  | { type: 'dataPoison'; targetId: EntityId }
  /** Guard a friendly unit/building: follow it and engage nearby threats. */
  | { type: 'guard'; unitIds: EntityId[]; targetId: EntityId }
  /** Patrol back and forth between two map points, engaging threats. */
  | { type: 'patrol'; unitIds: EntityId[]; ax: number; ay: number; bx: number; by: number }
  | { type: 'surrender' };

/** A command stamped with its issuer, as sequenced into a tick. */
export interface IssuedCommand {
  player: PlayerIdx;
  cmd: Command;
}

// ---------------------------------------------------------------------------
// Game state & config
// ---------------------------------------------------------------------------

export interface GameConfig {
  seed: number;
  players: PlayerConfig[];
  mapWidth?: number;
  mapHeight?: number;
  startingBudget?: number;
}

export interface GameEvent {
  tick: number;
  /** which player the event concerns; -1 = global */
  player: PlayerIdx;
  type:
    | 'anomalyStarted'
    | 'anomalyCleared'
    | 'insolvencyWarning'
    | 'playerEliminated'
    | 'phaseAdvanced'
    | 'techResearched'
    | 'gameOver'
    | 'underAttack'
    | 'brownout'
    | 'brownoutEnded'
    | 'doctrineChosen'
    | 'fineTuneComplete'
    | 'frontierAdvanced' // a player shipped a gen that MOVED the global frontier (everyone reprices)
    | 'modelShipped' // a player caught up to an existing generation (fast-follow)
    | 'marketMoved'
    | 'dataDiscovered'
    | 'cyberAttack' // a cyber op landed on this player (victim-facing)
    | 'cyberDefended'; // a cyber op was fully absorbed by this player's defenses
  message: string;
  entityId?: EntityId;
  /** playerEliminated only: the bare cause ("Cloud Console destroyed",
   *  "bankrupt") — the message wraps it in prose */
  reason?: string;
}

export interface GameState {
  tick: number;
  rngState: number;
  /** the config seed, persisted so a finished game can be identified/replayed */
  seed: number;
  map: GameMap;
  /** highest model generation any player has shipped; every serve-agent's
   *  revenue is priced relative to this (Economy 3.0: the frontier race) */
  frontierGen: number;
  players: PlayerState[];
  entities: Entity[];
  nextEntityId: number;
  /** set once a winner is decided; -1 = draw */
  winner: PlayerIdx | null;
  /** events emitted during the most recent tick (transient, for UI/bot) */
  events: GameEvent[];
  /** sampled series for the end-game graphs */
  timeline: Timeline;
}

// ---------------------------------------------------------------------------
// Multiplayer protocol (client <-> Durable Object)
// ---------------------------------------------------------------------------

/** Messages a client sends to the game room. */
export type ClientMessage =
  | {
      type: 'join';
      name: string;
      faction: FactionId;
      /** Optional ranked-account claim: the room forwards {name, secret} to
       * the Leaderboard Durable Object to verify/create the account and
       * binds the resulting playerId to this slot (async, never blocks the
       * join). `name` here is always the same string as the join's own
       * `name` field — you can't claim a ranked handle under a different
       * display name. */
      account?: { secret: string };
    }
  | { type: 'setFaction'; faction: FactionId }
  | { type: 'ready'; ready: boolean }
  | { type: 'start' } // host only
  | { type: 'commands'; tick: number; commands: Command[] }
  | { type: 'pong'; tick: number }
  /** Host only. Fills the next open slot with a bot, up to 4 total slots. */
  | { type: 'addBot'; faction?: FactionId; difficulty?: BotDifficulty }
  /** Host only. Pops the last bot slot, if any. */
  | { type: 'removeBot' }
  /** Sets the sender's own AI Copilot permission clearance (default 'economy'). */
  | { type: 'copilotMode'; mode: CopilotMode };

export interface LobbySlot {
  name: string;
  faction: FactionId;
  ready: boolean;
  connected: boolean;
  isHost: boolean;
  /** true for AI-filled slots added via 'addBot' (enables solo-with-copilot rooms). */
  isBot?: boolean;
  /** bot slots only */
  difficulty?: BotDifficulty;
}

/**
 * AI Copilot permission clearance for a seat, gating which MCP tools a
 * connected copilot may invoke on that player's behalf:
 *   'advise' -> observe + message the player only
 *   'economy' -> also manage the FinOps economy (build/train/research/etc.)
 *   'full'    -> also command combat (attack/move units)
 * Defaults to 'economy' for every human slot.
 */
export type CopilotMode = 'advise' | 'economy' | 'full';

/** Messages the game room broadcasts to clients. */
export type ServerMessage =
  // `copilotToken` is sent ONLY to that recipient's own socket (per-recipient
  // broadcast) -- it's the bearer secret for that slot's MCP endpoint.
  | { type: 'lobby'; slots: LobbySlot[]; you: number; copilotToken?: string }
  | { type: 'start'; config: GameConfig; you: number; copilotToken?: string }
  /** Full-state catch-up for a player rejoining an in-progress game: rebuild
   * the sim from `config`, then fast-forward by replaying `history` (the
   * sequenced command batch of every tick so far, in order). Subsequent
   * 'tick' messages continue from where the history ends. */
  | {
      type: 'resync';
      config: GameConfig;
      you: number;
      copilotToken?: string;
      history: IssuedCommand[][];
    }
  /** Sequenced batch for one tick: apply in order, then advance the sim. */
  | { type: 'tick'; tick: number; commands: IssuedCommand[] }
  | { type: 'playerLeft'; player: PlayerIdx }
  /** A disconnected seat's player reconnected mid-game. */
  | { type: 'playerRejoined'; player: PlayerIdx }
  | { type: 'ping'; tick: number }
  | { type: 'error'; message: string }
  /** An AI Copilot's advisory message to its own player, sent to that socket only. */
  | { type: 'copilot'; message: string }
  /** Achievements this player just unlocked (ranked accounts only), sent to
   * the earning player's socket only, shortly after the match is decided.
   * Ids index into the shared ACHIEVEMENTS registry. */
  | { type: 'achievements'; ids: string[] };
